import { defineStore } from 'pinia'

export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

export interface ToastItem {
  id: string
  title: string
  description?: string
  variant: ToastVariant
  duration: number
  open: boolean
  createdAt: number
}

export interface ToastInput {
  title: string
  description?: string
  variant?: ToastVariant
  duration?: number
}

export const DEFAULT_TOAST_DURATION = 5000
const REMOVE_DELAY = 200
let toastCounter = 0

function createToastId() {
  toastCounter += 1
  return `toast-${Date.now()}-${toastCounter}`
}

export const useToastStore = defineStore('toast', {
  state: () => ({
    toasts: [] as ToastItem[]
  }),
  actions: {
    notify(input: ToastInput) {
      const toast: ToastItem = {
        id: createToastId(),
        title: input.title,
        description: input.description,
        variant: input.variant ?? 'info',
        duration: input.duration ?? DEFAULT_TOAST_DURATION,
        open: true,
        createdAt: Date.now()
      }

      this.toasts.unshift(toast)
      return toast.id
    },
    success(title: string, description?: string) {
      return this.notify({ title, description, variant: 'success' })
    },
    error(title: string, description?: string) {
      return this.notify({ title, description, variant: 'error' })
    },
    warning(title: string, description?: string) {
      return this.notify({ title, description, variant: 'warning' })
    },
    info(title: string, description?: string) {
      return this.notify({ title, description, variant: 'info' })
    },
    dismiss(id: string) {
      const toast = this.toasts.find((item) => item.id === id)
      if (!toast) return

      toast.open = false
      setTimeout(() => {
        this.remove(id)
      }, REMOVE_DELAY)
    },
    remove(id: string) {
      this.toasts = this.toasts.filter((item) => item.id !== id)
    },
    clear() {
      this.toasts = []
    }
  }
})
